import { useState } from "react";
import { format } from "date-fns";
import {
  CreditCard,
  Ticket,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  EnrollmentBillingItem,
  EnrollmentRefundDetail,
} from "../../api/getEnrollmentBilling/types";
import { coverageBadge, stateBadge } from "./badges";
import axiosInstance from "@/api/axiosInstance";
import { useToast } from "@/hooks/useToast";

interface EnrollmentBillingDrawerProps {
  enrollment: EnrollmentBillingItem | null;
  open: boolean;
  onClose: () => void;
  onRefundSuccess: () => void;
}

function fmt(amount: string | number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(typeof amount === "string" ? parseFloat(amount) : amount);
}

function fmtDate(dateString: string | null) {
  if (!dateString) return "—";
  try {
    return format(new Date(dateString), "MMM d, yyyy");
  } catch {
    return dateString;
  }
}

/**
 * Refund amount is valid when it parses to a positive number that
 * doesn't exceed the remaining refundable. The BE re-validates the
 * cap, but client-side check disables the submit button so the user
 * doesn't bother round-tripping for a guaranteed-fail.
 */
function isRefundAmountValid(input: string, max: string): boolean {
  const n = parseFloat(input);
  const cap = parseFloat(max);
  if (!Number.isFinite(n) || n <= 0) return false;
  if (!Number.isFinite(cap)) return false;
  return n <= cap + 0.0001;
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors w-full text-left py-1"
        onClick={() => setOpen(!open)}
        type="button"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {title}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

export function EnrollmentBillingDrawer({
  enrollment,
  open,
  onClose,
  onRefundSuccess,
}: EnrollmentBillingDrawerProps) {
  const { toast } = useToast();
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [showVoucherReturnForm, setShowVoucherReturnForm] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [voucherReturnReason, setVoucherReturnReason] = useState("");
  const [isRefunding, setIsRefunding] = useState(false);
  const [isReturningVoucher, setIsReturningVoucher] = useState(false);

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setShowRefundForm(false);
      setShowVoucherReturnForm(false);
      setRefundReason("");
      setRefundAmount("");
      setVoucherReturnReason("");
      onClose();
    }
  }

  if (!enrollment) return null;

  const coverage = coverageBadge[enrollment.coverageType];
  const state = stateBadge(enrollment);
  const seatPrice = parseFloat(enrollment.seatPrice);
  const netAmount = parseFloat(enrollment.netAmount);
  const totalDiscount = parseFloat(enrollment.totalDiscountAmount);
  const refundedAmount = parseFloat(enrollment.refundedAmount);
  const remainingRefundable = parseFloat(enrollment.remainingRefundableAmount);

  const isCash = enrollment.coverageType === "cash";
  const isVoucher = enrollment.coverageType === "voucher";
  const isCancelled = enrollment.lifecycleState === "cancelled";

  const canRefund = isCash && remainingRefundable > 0;
  const canReturnVoucher = isVoucher && enrollment.voucherStatus === "applied";
  const recognitionPending =
    isVoucher &&
    enrollment.voucherStatus === "applied" &&
    !enrollment.voucherRecognized;

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" side="right">
        <SheetHeader className="pb-4">
          <div className="flex items-start justify-between gap-2 pr-6">
            <div>
              <SheetTitle className="text-base font-normal text-gray-900 dark:text-white">
                {enrollment.firstName} {enrollment.lastName}
              </SheetTitle>
              <SheetDescription className="text-xs mt-1">
                {enrollment.email}
                {enrollment.companyName && ` · ${enrollment.companyName}`}
                {enrollment.confirmationNumber &&
                  ` · #${enrollment.confirmationNumber}`}
              </SheetDescription>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0 mt-1">
              <span
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${coverage.style}`}
              >
                {coverage.label}
              </span>
              {state && (
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${state.style}`}
                >
                  {state.label}
                </span>
              )}
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-5">
          {/* Contact — parent EventCheckout's contact-person info */}
          {(enrollment.contactName ||
            enrollment.contactEmail ||
            enrollment.contactPhone ||
            enrollment.groupNotes) && (
            <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Contact
              </p>
              {enrollment.contactName && (
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {enrollment.contactName}
                </p>
              )}
              {enrollment.contactEmail && (
                <a
                  className="block text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  href={`mailto:${enrollment.contactEmail}`}
                >
                  {enrollment.contactEmail}
                </a>
              )}
              {enrollment.contactPhone && (
                <a
                  className="block text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  href={`tel:${enrollment.contactPhone}`}
                >
                  {enrollment.contactPhone}
                </a>
              )}
              {enrollment.groupNotes && (
                <div className="pt-1">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Group notes
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {enrollment.groupNotes}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Cancellation Notice */}
          {isCancelled && !enrollment.isRefunded && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-3">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                This enrollment has been cancelled
              </p>
              {enrollment.cancellationReason && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  Reason: {enrollment.cancellationReason}
                </p>
              )}
              <p className="text-xs text-amber-600 dark:text-amber-400/80 mt-2">
                {isCash
                  ? "Consider issuing a refund below."
                  : isVoucher
                    ? "Consider returning the voucher below."
                    : null}
              </p>
            </div>
          )}

          {/* Financial Summary */}
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">
                Seat Price
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {fmt(seatPrice)}
              </span>
            </div>
            {totalDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Discount
                  {enrollment.discounts[0]?.code && (
                    <span className="font-mono text-xs ml-1">
                      ({enrollment.discounts[0].code})
                    </span>
                  )}
                </span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  -{fmt(totalDiscount)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">
                Net Amount
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {fmt(netAmount)}
              </span>
            </div>
            {refundedAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Refunded
                </span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  -{fmt(refundedAmount)}
                </span>
              </div>
            )}
            {isCash && (
              <>
                <Separator className="!my-2" />
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Remaining Refundable
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {fmt(remainingRefundable)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Refund Details */}
          {enrollment.refundDetails.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                defaultOpen
                title={`Refund Details (${enrollment.refundDetails.length})`}
              >
                <div className="space-y-2">
                  {enrollment.refundDetails.map(
                    (refund: EnrollmentRefundDetail, i: number) => (
                      <div
                        className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 text-sm"
                        key={`refund-${i}`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white mb-1">
                          {fmt(refund.amount)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                          <p>
                            Transaction:{" "}
                            <span className="font-mono text-gray-900 dark:text-white">
                              {refund.transactionId}
                            </span>
                          </p>
                          {refund.reason && <p>Reason: {refund.reason}</p>}
                          {refund.refundedBy && (
                            <p>Refunded by: {refund.refundedBy}</p>
                          )}
                          {refund.refundedAt && (
                            <p>Date: {fmtDate(refund.refundedAt)}</p>
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* Cash Payment Details */}
          {isCash && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Payment Method
              </h4>
              <div className="flex items-center gap-2 text-sm">
                <CreditCard className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-900 dark:text-white">
                  Card ···· {enrollment.cardLast4 || "****"}
                </span>
              </div>
              {(enrollment.caraInvoiceId ||
                enrollment.caraPaymentIdentifier ||
                enrollment.transactionId) && (
                <CollapsibleSection title="Transaction Details">
                  <div className="space-y-1 text-xs">
                    {enrollment.caraInvoiceId && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Invoice
                        </span>
                        <span className="font-mono text-gray-900 dark:text-white">
                          {enrollment.caraInvoiceId}
                        </span>
                      </div>
                    )}
                    {enrollment.caraPaymentIdentifier && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Payment ID
                        </span>
                        <span className="font-mono text-gray-900 dark:text-white">
                          {enrollment.caraPaymentIdentifier}
                        </span>
                      </div>
                    )}
                    {enrollment.transactionId && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Gateway Txn
                        </span>
                        <span className="font-mono text-gray-900 dark:text-white">
                          {enrollment.transactionId}
                        </span>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}
            </div>
          )}

          {/* Voucher Details */}
          {isVoucher && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Voucher
              </h4>
              <div className="flex items-center gap-2 text-sm">
                <Ticket className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-900 dark:text-white">
                  {enrollment.voucherName || "Voucher"}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                {enrollment.voucherRedeemedAt && (
                  <p>Redeemed: {fmtDate(enrollment.voucherRedeemedAt)}</p>
                )}
                {enrollment.voucherStatus === "returned" ? (
                  <>
                    <p>Returned: {fmtDate(enrollment.voucherReturnedAt)}</p>
                    {enrollment.voucherReturnReason && (
                      <p className="italic">
                        Reason: {enrollment.voucherReturnReason}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400 dark:text-gray-500">Active</p>
                )}
                {enrollment.voucherRecognized && (
                  <p>
                    Recognized: {fmtDate(enrollment.voucherRecognizedAt)}
                    {enrollment.voucherRecognitionInvoiceId && (
                      <>
                        {" "}
                        ·{" "}
                        <span className="font-mono">
                          {enrollment.voucherRecognitionInvoiceId}
                        </span>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Discounts (if multiple) */}
          {enrollment.discounts.length > 1 && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Discounts
                </h4>
                {enrollment.discounts.map((d, i) => (
                  <div
                    className="flex justify-between text-sm"
                    key={`${d.source}-${i}`}
                  >
                    <span className="text-gray-500 dark:text-gray-400">
                      {d.code ? (
                        <span className="font-mono text-xs">{d.code}</span>
                      ) : (
                        d.source
                      )}
                      {d.reason && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                          — {d.reason}
                        </span>
                      )}
                    </span>
                    <span className="text-green-600 dark:text-green-400">
                      -{fmt(d.appliedAmount)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Voucher recognition pending */}
          {recognitionPending && (
            <>
              <Separator />
              <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2.5">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                  Voucher Recognition Pending
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400/80 mt-0.5">
                  Revenue will be booked at CARA when the session is finalized.
                </p>
              </div>
            </>
          )}

          {/* Refund Action */}
          {canRefund && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Issue Refund
                </h4>
                {!showRefundForm ? (
                  <Button
                    className="w-full"
                    onClick={() => {
                      // Pre-fill with the remaining refundable so the
                      // common case (full refund of what's left) is
                      // one click. User can edit down for partials.
                      setRefundAmount(enrollment.remainingRefundableAmount);
                      setShowRefundForm(true);
                    }}
                    variant="outline"
                  >
                    Issue Refund
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label
                        className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
                        htmlFor="refund-amount"
                      >
                        Refund amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-gray-500">
                          $
                        </span>
                        <input
                          className="flex h-9 w-full rounded-md border border-input bg-white pl-6 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                          id="refund-amount"
                          inputMode="decimal"
                          max={enrollment.remainingRefundableAmount}
                          min="0.01"
                          onChange={(e) => setRefundAmount(e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          type="number"
                          value={refundAmount}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Up to{" "}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {fmt(enrollment.remainingRefundableAmount)}
                        </span>{" "}
                        refundable.
                      </p>
                    </div>
                    <Textarea
                      className="resize-none text-sm"
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Reason for refund..."
                      rows={3}
                      value={refundReason}
                    />
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        disabled={isRefunding}
                        onClick={() => {
                          setShowRefundForm(false);
                          setRefundReason("");
                          setRefundAmount("");
                        }}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1"
                        disabled={
                          !refundReason.trim() ||
                          isRefunding ||
                          !isRefundAmountValid(
                            refundAmount,
                            enrollment.remainingRefundableAmount,
                          )
                        }
                        onClick={async () => {
                          setIsRefunding(true);
                          try {
                            await axiosInstance.post(
                              `/api/private/enrollments/${enrollment.enrollmentId}/refund`,
                              {
                                reason: refundReason.trim(),
                                amount: refundAmount,
                                notifyAttendee: true,
                              },
                            );
                            toast({
                              title: "Refund initiated",
                              description: `${fmt(refundAmount)} refunded to ${enrollment.firstName} ${enrollment.lastName}.`,
                            });
                            onRefundSuccess();
                            setShowRefundForm(false);
                            setRefundReason("");
                            setRefundAmount("");
                          } catch {
                            // Error toast handled by axios interceptor
                          } finally {
                            setIsRefunding(false);
                          }
                        }}
                        variant="default"
                      >
                        {isRefunding && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Confirm Refund
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Voucher Return Action */}
          {canReturnVoucher && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Return Voucher
                </h4>
                {!showVoucherReturnForm ? (
                  <Button
                    className="w-full"
                    onClick={() => setShowVoucherReturnForm(true)}
                    variant="outline"
                  >
                    Return Voucher
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Textarea
                      className="resize-none text-sm"
                      onChange={(e) => setVoucherReturnReason(e.target.value)}
                      placeholder="Reason for voucher return..."
                      rows={3}
                      value={voucherReturnReason}
                    />
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        disabled={isReturningVoucher}
                        onClick={() => {
                          setShowVoucherReturnForm(false);
                          setVoucherReturnReason("");
                        }}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1"
                        disabled={
                          !voucherReturnReason.trim() || isReturningVoucher
                        }
                        onClick={async () => {
                          setIsReturningVoucher(true);
                          try {
                            await axiosInstance.post(
                              `/api/private/enrollments/${enrollment.enrollmentId}/return-voucher`,
                              { reason: voucherReturnReason.trim() },
                            );
                            toast({
                              title: "Voucher returned",
                              description: `Voucher for ${enrollment.firstName} ${enrollment.lastName} has been returned.`,
                            });
                            onRefundSuccess();
                            setShowVoucherReturnForm(false);
                            setVoucherReturnReason("");
                          } catch {
                            // Error toast handled by axios interceptor
                          } finally {
                            setIsReturningVoucher(false);
                          }
                        }}
                        variant="default"
                      >
                        {isReturningVoucher && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Confirm Return
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
